/**
 * Bestätigungsstreifen unten am Bildschirm (Spec 04b, „Sheet Zahlung
 * erfassen": „Sheet schließt … Ein Streifen unten bestätigt"). Lebt
 * außerhalb des Sheets, das ihn auslöst — deshalb hier, statt in
 * `features/billing`, und von der jeweiligen Route eingesetzt.
 *
 * Kein Toast-System, keine neue Abhängigkeit (CLAUDE.md Regel 11): Es läuft
 * nie mehr als eine Bestätigung gleichzeitig, weil pro Bildschirm immer nur
 * ein Sheet offen ist — ein einzelner, sich selbst schließender Streifen
 * genügt.
 *
 * Spec 05a erweitert die Rolle um eine **Rückfrage** vor einer zerstörenden
 * Aktion („Entziehen fragt zurück"): derselbe Streifen, aber mit zwei Knöpfen
 * statt eines Automatik-Timers — die Entscheidung liegt beim Nutzer, nicht
 * bei der Uhr. Ohne `confirm` bleibt das bisherige Verhalten unverändert; die
 * bestehenden Aufrufer (`routes/Geld.tsx`, `features/billing/
 * TenantLedger.tsx`) brauchen keine Änderung. Spec 07 nimmt „Heute" den
 * dritten Aufrufer wieder ab — der Kalender bestätigt einen angelegten
 * Termin nicht über diesen Streifen (`DueDateSheet` kennt kein `onSaved`,
 * dieselbe Zurückhaltung wie am Objekt, 06b).
 */

import { useEffect, useRef } from 'react'

const AUTO_DISMISS_MS = 4000

interface ConfirmationStripConfirm {
  readonly confirmLabel: string
  readonly confirmTestId: string
  readonly onConfirm: () => void
  readonly cancelLabel: string
  readonly cancelTestId: string
}

interface ConfirmationStripProps {
  /** `null` → nichts anzuzeigen. */
  readonly message: string | null
  /** Ohne `confirm`: Automatik-Timer läuft ab. Mit `confirm`: `Esc` oder
   * „Abbrechen" rufen dasselbe auf — „Abbrechen schickt nichts" (Spec 05a). */
  readonly onDismiss: () => void
  readonly testId: string
  /** Gesetzt für eine Rückfrage vor einer zerstörenden Aktion: zwei Knöpfe,
   * kein Automatik-Timer, „Abbrechen" ist beim Öffnen fokussiert — eine
   * zerstörende Aktion ist nie der Standardfokus (`design-system`). */
  readonly confirm?: ConfirmationStripConfirm
}

export function ConfirmationStrip({ message, onDismiss, testId, confirm }: ConfirmationStripProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Voll ausgeschriebene Abhängigkeiten, keine Unterdrückung: `confirm`/
  // `onDismiss` sind beim Aufrufer Inline-Literale und damit bei jedem
  // Rendern neue Referenzen — der Effekt läuft dadurch auch bei einem
  // Rendern erneut, das `message` nicht ändert, und fokussiert „Abbrechen"
  // bzw. setzt den Timer dann neu. Dieselbe Toleranz trug die Komponente
  // schon vor dieser Erweiterung (Abhängigkeit `onDismiss` im ursprünglichen
  // Timer-Effekt); an drei bestehenden Aufrufern war das nie ein Befund.
  useEffect(() => {
    if (message === null) return
    if (confirm !== undefined) {
      cancelRef.current?.focus()
      return
    }
    const timer = window.setTimeout(onDismiss, AUTO_DISMISS_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [message, confirm, onDismiss])

  useEffect(() => {
    if (message === null || confirm === undefined) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [message, confirm, onDismiss])

  if (message === null) return null

  return (
    <div
      data-testid={testId}
      role="status"
      // `bottom-16` unter dem `md:`-Umbruch: Die mobile Tab-Leiste
      // (`AppShell.tsx`, `MobileTabs`) ist mindestens `--tap-min` (44px) hoch
      // und liegt `fixed` ohne eigenen `z-index` — bei `bottom-4` gewann der
      // Streifen sonst gegen sie und verdeckte das mittlere Ziel
      // (Gestaltungswarnung W2, `reports/12-design-reviewer.md`). Derselbe
      // Wert, den `main` bereits als `pb-16` für genau diese Leiste reserviert
      // (`AppShell.tsx`).
      //
      // `z-[60]`, nicht `z-40`: Seit Spec 05a öffnet `access-revoke-<id>` die
      // Rückfrage, während `access-sheet` offen ist — dessen Radix-Overlay
      // und -Inhalt liegen beide auf `z-50` (`components/ui/sheet.tsx`,
      // shadcn-Generat, nicht editierbar). Mit `z-40` lag der Streifen
      // sichtbar, aber unter dem Overlay: „Abbrechen"/„Entziehen" nahmen
      // keinen Klick an — beim Aufnehmen der Bildschirmfotos für diesen Lauf
      // gefunden, kein Vermuten.
      className="border-border bg-surface rounded-card fixed inset-x-0 bottom-16 z-[60] mx-auto flex w-fit items-center gap-2 border px-4 py-2 md:bottom-4 print:hidden"
    >
      <span className="text-body text-text">{message}</span>
      {confirm !== undefined && (
        <div className="flex items-center gap-2">
          <button
            ref={cancelRef}
            type="button"
            data-testid={confirm.cancelTestId}
            onClick={onDismiss}
            className="text-text hover:bg-bg rounded-card text-label px-3 py-1 transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            {confirm.cancelLabel}
          </button>
          <button
            type="button"
            data-testid={confirm.confirmTestId}
            onClick={confirm.onConfirm}
            className="bg-status-overdue rounded-card text-label px-3 py-1 text-white transition-opacity duration-(--duration-fast) ease-(--ease-out)"
          >
            {confirm.confirmLabel}
          </button>
        </div>
      )}
    </div>
  )
}
